import * as React from 'react'
import { redirect } from 'react-router-dom'

import {
  BankAccountRoutesDocument,
  FinanceTreasuryModuleRoutesVisibilityDocument,
  PaymentPageRoutesDocument,
  PaymentRequestDetailRoutesDocument,
  TreasuryPaymentsRoutesDocument,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { buildModuleRoute, buildNavigation } from 'modules/core/lib/react-router'

import { buildBankAccountMeta } from 'modules/banking/routes/buildBankAccountMeta'
import { buildPaymentHeaderComponentProps } from 'modules/banking/routes/resources_headers/payment'
import { buildPaymentRequestHeaderComponentProps } from 'modules/banking/routes/resources_headers/paymentRequest'

const BankingCardsPage = React.lazy(() => import('modules/banking/pages/CardsPage'))

const ReroutingIndex = React.lazy(() => import('modules/finance/pages/ReroutingIndex'))

const TreasuryOverviewPage = React.lazy(() => import('modules/finance/pages/TreasuryOverviewPage'))

const PaymentsPage = React.lazy(() => import('modules/banking/pages/PaymentsPage'))
const TransfersPage = React.lazy(() => import('modules/banking/pages/TransfersPage'))
const PaymentRequestsPage = React.lazy(() => import('modules/banking/pages/PaymentRequestsPage'))
const PaymentRequestDetailPage = React.lazy(
  () => import('modules/banking/pages/PaymentRequestDetailPage')
)

const RootPage = React.lazy(() => import('modules/banking/pages/RootPage'))

const AccountCardsDataTable = React.lazy(
  () => import('modules/banking/components/AccountCardsDataTable')
)
const AccountTransactionsDataTable = React.lazy(
  () => import('modules/banking/components/AccountTransactionsDataTable')
)
const AccountCardPaymentsDataTable = React.lazy(
  () => import('modules/banking/components/AccountCardPaymentsDataTable')
)

const TransactionDetailSidePanel = React.lazy(
  () => import('modules/banking/components/AccountTransactionsDataTable/TransactionDetailSidePanel')
)

const AccountPage = React.lazy(() => import('modules/banking/pages/AccountPage'))
const AccountsListPage = React.lazy(() => import('modules/banking/pages/AccountsListPage'))

const BankingReconciliationSidePanel = React.lazy(
  () => import('modules/banking/components/BankingReconciliationSidePanel')
)

// TODO: Check if PaymentPage can be deleted after BulkPaymentDetailPage is stable
const BulkPaymentDetailPage = React.lazy(
  () => import('modules/banking/pages/BulkPaymentDetailPage')
)

export const financeTreasuryRoutes = buildNavigation((navigation) => {
  return [
    buildModuleRoute({
      path: navigation.finance.treasury.path,
      element: <RootPage />,
      handle: {
        module: 'finance-treasury',
        moduleName: () => i18n.t('sidebar.finance_treasury'),
        meta: ({ watchQuery }) => {
          return watchQuery({
            query: FinanceTreasuryModuleRoutesVisibilityDocument,
          }).map(({ data }) => {
            const {
              sidebarSeeFinancePaymentsSubLevelItem,
              sidebarSeeFinanceCardsSubLevelItem,
              sidebarSeeFinanceAccountsSubLevelItem,
            } = data.finance._policies
            return {
              title: i18n.t('sidebar.finance_treasury'),
              subNavigation: [
                {
                  id: 'overview',
                  title: i18n.t('finance.modules.treasury.overview'),
                  href: navigation.finance.treasury.overview.fullPath(),
                  isVisible: sidebarSeeFinanceAccountsSubLevelItem.can,
                },
                {
                  id: 'banks',
                  title: i18n.t('finance.modules.treasury.banks'),
                  href: navigation.finance.treasury.banking.accounts.fullPath(),
                  isVisible: sidebarSeeFinanceAccountsSubLevelItem.can,
                },
                {
                  id: 'cards',
                  title: i18n.t('finance.modules.spending.cards'),
                  href: navigation.finance.treasury.banking.cards.fullPath(),
                  isVisible: sidebarSeeFinanceCardsSubLevelItem.can,
                },
                {
                  id: 'payments',
                  title: i18n.t('finance.modules.treasury.payments'),
                  href: navigation.finance.treasury.banking.paymentRequests.fullPath(),
                  isVisible: sidebarSeeFinancePaymentsSubLevelItem.can,
                },
              ],
            }
          })
        },
      },
      children: [
        {
          index: true,
          element: <ReroutingIndex />,
        },
        {
          path: navigation.finance.treasury.overview.path,
          element: <TreasuryOverviewPage />,
        },
        {
          path: navigation.finance.treasury.banking.path,
          children: [
            {
              path: navigation.finance.treasury.banking.accounts.path,
              children: [
                {
                  index: true,
                  element: <AccountsListPage />,
                },
                {
                  path: navigation.finance.treasury.banking.accounts.accountId.path,
                  element: <AccountPage />,
                  handle: {
                    resource: true,
                    meta: ({ params, watchQuery }) =>
                      params['id']
                        ? watchQuery({
                            query: BankAccountRoutesDocument,
                            variables: { id: Number(params['id']) },
                          }).map(({ data }) => {
                            return buildBankAccountMeta(navigation, data)
                          })
                        : { title: '' },
                  },

                  children: [
                    {
                      index: true,
                      loader: ({ params }) =>
                        redirect(
                          navigation.finance.treasury.banking.accounts.accountId.transactions.fullPath(
                            {
                              id: params.id + '',
                            }
                          )
                        ),
                    },
                    {
                      path: navigation.finance.treasury.banking.accounts.accountId.transactions
                        .path,

                      children: [
                        {
                          path: navigation.finance.treasury.banking.accounts.accountId.transactions
                            .transactionId.path,
                          element: <TransactionDetailSidePanel />,
                        },
                      ],
                      element: <AccountTransactionsDataTable />,
                    },
                    {
                      path: navigation.finance.treasury.banking.accounts.accountId.cards.path,
                      element: <AccountCardsDataTable />,
                    },
                    {
                      path: navigation.finance.treasury.banking.accounts.accountId.card_payments
                        .path,
                      element: <AccountCardPaymentsDataTable />,
                    },
                    {
                      path: navigation.finance.treasury.banking.accounts.accountId.reconciliations
                        .path,

                      handle: {
                        meta: () => ({ title: i18n.t('banking.accounts.reconciliations') }),
                      },
                      element: <BankingReconciliationSidePanel />,
                    },
                  ],
                },
              ],
            },
            {
              path: navigation.finance.treasury.banking.payments.path,
              handle: {
                // TODO: When FF 'dev_banking_payment_requests' is removed, simplify this to a static meta:
                // meta: () => ({
                //   title: i18n.t('banking.payments.title'),
                //   illustration: 'coins',
                //   description: i18n.t('banking.payments.description2'),
                //   subNavigation: [
                //     { id: 'paymentRequests', title: i18n.t('banking.paymentRequests.title'), href: navigation.finance.treasury.banking.paymentRequests.fullPath() },
                //     { index: true, id: 'payments', title: i18n.t('banking.payments.title'), href: navigation.finance.treasury.banking.payments.fullPath() },
                //   ],
                // }),
                // Also remove the 'transfers' tab and TreasuryPaymentsRoutesDocument query.
                meta: ({ watchQuery, session }) =>
                  watchQuery({
                    query: TreasuryPaymentsRoutesDocument,
                    variables: {
                      companyId: session.company.id,
                      featureNames: ['dev_banking_payment_requests'],
                    },
                  }).map(({ data }) => {
                    const isPaymentRequestsEnabled =
                      data?.features?.featuresConnection?.nodes.find(
                        (feature) => feature.name === 'dev_banking_payment_requests'
                      )?.enabled ?? false

                    return {
                      title: i18n.t('banking.payments.title'),
                      illustration: 'coins',
                      description: i18n.t('banking.payments.description2'),
                      subNavigation: [
                        {
                          id: 'paymentRequests',
                          title: i18n.t('banking.paymentRequests.title'),
                          href: navigation.finance.treasury.banking.paymentRequests.fullPath(),
                          isVisible: isPaymentRequestsEnabled,
                        },
                        {
                          index: true,
                          id: 'payments',
                          title: i18n.t('banking.payments.title'),
                          href: navigation.finance.treasury.banking.payments.fullPath(),
                        },
                        {
                          id: 'transfers',
                          title: i18n.t('banking.transfers.page.title'),
                          href: navigation.finance.treasury.banking.payments.transfers.fullPath(),
                          isVisible: !isPaymentRequestsEnabled,
                        },
                      ],
                    }
                  }),
              },
              children: [
                {
                  index: true,
                  element: <PaymentsPage />,
                },
                {
                  path: navigation.finance.treasury.banking.payments.paymentId.path,
                  handle: {
                    resource: true,
                    meta: ({ params, watchQuery, session }) =>
                      params['id']
                        ? watchQuery({
                            query: PaymentPageRoutesDocument,
                            variables: {
                              id: Number(params['id']),
                              companyId: session.company.id,
                              featureNames: ['dev_finance_swan_reactivation'],
                            },
                          }).map(({ data }) => {
                            const canSeeSepaTransferAction =
                              data?.features?.featuresConnection?.nodes.find(
                                (feature) => feature.name === 'dev_finance_swan_reactivation'
                              )?.enabled ?? false
                            const payment = data?.banking?.payment
                            if (!payment) {
                              return { title: '' }
                            }
                            return {
                              title: payment.label,
                              headerComponentProps: buildPaymentHeaderComponentProps(
                                payment,
                                canSeeSepaTransferAction
                              ),
                            }
                          })
                        : { title: '' },
                  },
                  element: <BulkPaymentDetailPage />,
                },
                {
                  path: navigation.finance.treasury.banking.payments.transfers.path,
                  handle: {
                    meta: () => ({
                      title: i18n.t('banking.transfers.page.title'),
                    }),
                  },
                  element: <TransfersPage />,
                },
              ],
            },
            {
              path: navigation.finance.treasury.banking.paymentRequests.path,
              handle: {
                // TODO: When FF 'dev_banking_payment_requests' is removed, simplify this to a static meta:
                // meta: () => ({
                //   title: i18n.t('banking.paymentRequests.title'),
                //   illustration: 'coins',
                //   description: i18n.t('banking.payments.description2'),
                //   subNavigation: [
                //     { index: true, id: 'paymentRequests', title: i18n.t('banking.paymentRequests.title'), href: navigation.finance.treasury.banking.paymentRequests.fullPath() },
                //     { id: 'payments', title: i18n.t('banking.payments.title'), href: navigation.finance.treasury.banking.payments.fullPath() },
                //   ],
                // }),
                // Also remove the 'transfers' tab and TreasuryPaymentsRoutesDocument query.
                meta: ({ watchQuery, session }) =>
                  watchQuery({
                    query: TreasuryPaymentsRoutesDocument,
                    variables: {
                      companyId: session.company.id,
                      featureNames: ['dev_banking_payment_requests'],
                    },
                  }).map(({ data }) => {
                    const isPaymentRequestsEnabled =
                      data?.features?.featuresConnection?.nodes.find(
                        (feature) => feature.name === 'dev_banking_payment_requests'
                      )?.enabled ?? false

                    return {
                      title: i18n.t('banking.paymentRequests.title'),
                      illustration: 'coins',
                      description: i18n.t('banking.payments.description2'),
                      subNavigation: [
                        {
                          index: true,
                          id: 'paymentRequests',
                          title: i18n.t('banking.paymentRequests.title'),
                          href: navigation.finance.treasury.banking.paymentRequests.fullPath(),
                          isVisible: isPaymentRequestsEnabled,
                        },
                        {
                          id: 'payments',
                          title: i18n.t('banking.payments.title'),
                          href: navigation.finance.treasury.banking.payments.fullPath(),
                        },
                        {
                          id: 'transfers',
                          title: i18n.t('banking.transfers.page.title'),
                          href: navigation.finance.treasury.banking.payments.transfers.fullPath(),
                          isVisible: !isPaymentRequestsEnabled,
                        },
                      ],
                    }
                  }),
              },
              children: [
                {
                  index: true,
                  element: <PaymentRequestsPage />,
                },
                {
                  path: navigation.finance.treasury.banking.paymentRequests.paymentRequestId.path,
                  handle: {
                    resource: true,
                    meta: ({ params, watchQuery, session }) =>
                      params['paymentRequestId']
                        ? watchQuery({
                            query: PaymentRequestDetailRoutesDocument,
                            fetchPolicy: 'cache-first',
                            variables: {
                              id: Number(params['paymentRequestId']),
                              companyId: session.company.id,
                              featureNames: ['dev_banking_payment_requests'],
                            },
                          }).map(({ data }) => {
                            const isFeatureEnabled =
                              data?.features?.featuresConnection?.nodes.find(
                                (feature: { name: string; enabled: boolean }) =>
                                  feature.name === 'dev_banking_payment_requests'
                              )?.enabled ?? false

                            const paymentRequest = data?.banking?.paymentRequest
                            if (!paymentRequest || !isFeatureEnabled) {
                              return { title: '' }
                            }

                            return {
                              title: paymentRequest.label ?? '',
                              headerComponentProps:
                                buildPaymentRequestHeaderComponentProps(paymentRequest),
                            }
                          })
                        : { title: '' },
                  },
                  element: <PaymentRequestDetailPage />,
                },
              ],
            },
            {
              path: navigation.finance.treasury.banking.cards.path,
              element: <BankingCardsPage />,
              handle: {
                meta: () => {
                  return {
                    title: i18n.t('finance.sidebar.cards'),
                    description: i18n.t('finance.cards.description'),
                    illustration: 'credit-card',
                  }
                },
              },
              children: [
                {
                  path: navigation.finance.treasury.banking.cards.cardId.path,
                  loader: ({ params }) => {
                    const cardId = params.cardId
                    const redirectUrl = `${navigation.finance.treasury.banking.cards.fullPath()}?showCard=${cardId}`
                    return redirect(redirectUrl)
                  },
                },
              ],
            },
          ],
        },
      ],
    }),
  ]
})
